import ocaFieldsConfig from '../pages/DynamicOCA/config/fields_sectionized.json';
import referencesFields from '../pages/DynamicOCA/config/References/referencesFields.json';
import equipmentDetailsOCAFields from '../pages/DynamicOCA/config/EquipmentDetails/equipmentDetailsFields.json';

// constants
import {
  configurableItemsTypes,
  fieldsToNotPopulateFromPocForBusinessApp,
  ocaSectionTypes,
  pocFields,
  portalConfigurationTypes,
  taskRegardingValues,
} from '../pages/Prequal/constants';
import { getItemDefaultRolesSettings, rolesSettingsKeys } from './pcwService';
import { nonCreditMgrsRoles, vendorContactRole } from '../utils';

// libraries
import _ from 'lodash';
import { format } from 'date-fns';

const config = require('../config');

const ocaFieldList = _.flatMap(ocaFieldsConfig, 'fieldList');
const ocaFieldListHashMap = _.keyBy(ocaFieldList, 'config.fieldName');
ocaFieldListHashMap['equipmentFinancing'] = ocaFieldListHashMap['amountRequested']; // for backward compatibility with prev used key
const ocaDynamicReferencesFieldsHashMap = _.keyBy(referencesFields, 'config.fieldName');
const ocaEquipmentDetailsFieldsHashMap = _.keyBy(equipmentDetailsOCAFields, 'config.fieldName');

const referencesSettings = [
  {
    referenceType: 'BANK',
    mappings: {
      companyName: 'primaryBankName',
      contactName: 'primaryBankContactName',
      contactEmail: 'primaryBankContactEmail',
      contactPhone: 'primaryBankPhone',
      accountNum: 'primaryBankAccountNum',
      currentBalance: 'primaryBankAverageBalance',
    },
  },
  {
    referenceType: 'LOAN',
    mappings: {
      companyName: 'primaryCreditName',
      contactName: 'primaryCreditContactName',
      contactEmail: 'primaryCreditContactEmail',
      contactPhone: 'primaryCreditPhone',
      accountNum: 'primaryCreditAccountNum',
    },
  },
  {
    referenceType: 'LOC',
    mappings: {
      creditLimit: 'locReferenceCreditLimit',
      currentBalance: 'locReferenceCurrentBalance',
      monthlyPayment: 'locReferenceMonthlyPayment',
      renewalDate: 'locReferenceRenewalDate',
      lineOfCredit: 'locReferenceLineOfCredit',
    },
  },
  {
    referenceType: 'TRADE',
    mappings: {
      companyName: 'tradeReferenceName',
      contactName: 'tradeReferenceContactName',
      contactEmail: 'tradeReferenceContactEmail',
      contactPhone: 'tradeReferencePhone',
      contactFax: 'tradeReferenceFax',
      accountNum: 'tradeReferenceAccountNum',
      percRevenue: 'percRevenue',
      years: 'tradeReferenceYears',
      months: 'tradeReferenceMonths',
    },
  },
  {
    referenceType: 'TRADE_TWO',
    mappings: {
      companyName: 'tradeReferenceName2',
      contactName: 'tradeReferenceContactName2',
      contactEmail: 'tradeReferenceContactEmail2',
      contactPhone: 'tradeReferencePhone2',
      contactFax: 'tradeReferenceFax2',
      accountNum: 'tradeReferenceAccountNum2',
      percRevenue: 'percRevenue2',
      years: 'tradeReferenceYears2',
      months: 'tradeReferenceMonths2',
    },
  },
  {
    referenceType: 'TRADE_THREE',
    mappings: {
      companyName: 'tradeReferenceName3',
      contactName: 'tradeReferenceContactName3',
      contactEmail: 'tradeReferenceContactEmail3',
      contactPhone: 'tradeReferencePhone3',
      contactFax: 'tradeReferenceFax3',
      accountNum: 'tradeReferenceAccountNum3',
      percRevenue: 'percRevenue3',
      years: 'tradeReferenceYears3',
      months: 'tradeReferenceMonths3',
    },
  },
];

const refSettingsByRefType = _.keyBy(referencesSettings, 'referenceType');

const sectionTypes = {
  disclosures: 'disclosures',
};

const blockTypes = {
  reference: 'reference', // legacy references
  dynamicReference: ocaSectionTypes.dynamicReference,
  owner: 'owner',
  coApplicant: ocaSectionTypes.coApplicant,
  equipment: ocaSectionTypes.equipment,
};

const defaultMovingElementSettings = {
  sectionIndex: null,
  blockIndex: null,
  fieldIndex: null,
  type: null,
  action: null,
  refEl: null,
};

// Field keys correspond to logic on apiMapping (online-credit-app)
const referenceTypesByLegacyFieldsKey = {
  primaryBankName: 'BANK',
  loanReferenceBankName: 'LOAN',
  primaryCreditName: 'LOAN',
  locInPlace: 'LOC',
  tradeReferenceName: 'TRADE',
  tradeReferenceName2: 'TRADE_TWO',
  tradeReferenceName3: 'TRADE_THREE',
};

const uneditableFieldTypes = [
  'document-question',
  'image-input',
  'signatureselect-field',
  'sign-field',
  'signor-availability-question',
  'dynamics-disclosure-field',
  'html-output-field',
];

const uneditableFieldKeys = [
  'applyingSelect',
  'ccg_transaction_equipmentdescription',
  'bizOrIndividual',
  'billingAddressDifferentFromBusinessAddress',
  'locInPlace',
  'insuranceWaiver',
];

const fieldTypesToSkipForPCW = ['spacer-field'];

const ownersHardcodedKeys = ['primaryContact', 'owners[1]'];
const fieldsForCreditMgrsOnly = ['ssn', 'dateOfBirth', 'homeAddress'];
const hiddenFields = ['haveCoApplicant'];
const hiddenFieldsOnPDF = ['signatureDate', 'personalGuaranteeSignatureDate'];
const readOnlyFieldsByRoles = {
  signatureDate: [vendorContactRole.sales_rep],
  personalGuaranteeSignatureDate: [vendorContactRole.sales_rep],
};
const ambiguousFieldsToReplaceMap = {
  homeAddress: 'address',
  homeCity: 'city',
  homeState: 'state',
  homePostalCode: 'postalCode',
  percOwner: 'ownerPercentage',
  employerPhone: 'workPhone',
  driverLicenseNum: 'driversLicenseNum',
  equipmentFinancing: 'amountRequested',
  ccg_transaction_equipmentdescription: 'equipmentDescription',
};

_.forEach(ownersHardcodedKeys, ownerKey => {
  // set ownersHardcodedKeys to ambiguousFieldsToReplaceMap
  _.forEach(ambiguousFieldsToReplaceMap, (normalKey, ambiguousKey) => {
    ambiguousFieldsToReplaceMap[`${ownerKey}.${ambiguousKey}`] = `${ownerKey}.${normalKey}`;
  });
  // add ownersHardcodedKeys to fieldsForCreditMgrsOnly
  _.forEach(fieldsForCreditMgrsOnly, fieldForCreditMgrsOnly => {
    fieldsForCreditMgrsOnly.push(`${ownerKey}.${fieldForCreditMgrsOnly}`);
  });
  // add ownersHardcodedKeys to hiddenFieldsOnPDF
  _.forEach(hiddenFieldsOnPDF, hiddenFieldOnPDF => {
    hiddenFieldsOnPDF.push(`${ownerKey}.${hiddenFieldOnPDF}`);
  });
  // add ownersHardcodedKeys to uneditableFieldKeys
  _.forEach(uneditableFieldKeys, uneditableFieldKey => {
    uneditableFieldKeys.push(`${ownerKey}.${uneditableFieldKey}`);
  });
});

const getCreditAppModalDefaultPortalConfiguration = (vendorProfileId, type) => {
  return {
    vendorProfileId,
    jsonDefinition: getCreditAppModalDefaultJSONDefinition(),
    type,
  };
};

const getCreditAppModalDefaultJSONDefinition = () => {
  const defaultSectionConfig = getItemDefaultConfig(configurableItemsTypes.section);
  const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
  return {
    config: {
      title: 'Credit Application Detail',
    },
    sections: [
      {
        ...defaultSectionConfig,
        blocks: [defaultBlockConfig],
      },
    ],
  };
};

const assignFieldsOCAConfig = jsonDefinition => {
  const allBlocks = _.flatMap(jsonDefinition.sections, 'blocks');
  const ocaFieldsHashMapByBlockType = {
    [blockTypes.dynamicReference]: ocaDynamicReferencesFieldsHashMap,
    [blockTypes.equipment]: ocaEquipmentDetailsFieldsHashMap,
  };
  _.forEach(allBlocks, block => {
    //const isComplexKeyUsed = _.includes([blockTypes.dynamicReference, blockTypes.coApplicant], block.type);
    const ocaFieldsHashMap = ocaFieldsHashMapByBlockType[block.type]
      ? ocaFieldsHashMapByBlockType[block.type]
      : ocaFieldListHashMap;
    _.forEach(block.fields, field => {
      const keyToUse = field.ambiguousKey || field.key; // ambiguous key is in priority
      const isComplexKeyUsed = _.includes(keyToUse, '.');
      const ocaKey = isComplexKeyUsed ? keyToUse.slice(field.key.lastIndexOf('.') + 1) : keyToUse;
      let ocaField = ocaFieldsHashMap[ocaKey];
      if (ocaField) {
        field.ocaConfig = ocaField;
      }
    });
  });
};

// use oca config from active template. assignFieldsOCAConfig was prev used (fields config from fields_sectionized file was used)
const assignFieldsOCAConfigFromActiveTemplate = (pcwJsonDefinition, ocaTemplateJsonDefinition) => {
  const getFieldOcaConfig = ocaField => {
    const getFieldOptionsByYearsSettings = yearsSettings => {
      let maxYear = yearsSettings.maxYear;
      if (maxYear === '') {
        // current year
        maxYear = yearsSettings.yearsInAdvance
          ? new Date().getFullYear() + yearsSettings.yearsInAdvance
          : new Date().getFullYear();
      }
      const minYear = yearsSettings.minYear || new Date().getFullYear();
      const yearsRange = _.range(maxYear, minYear - 1);
      return _.map(yearsRange, year => ({
        optionLabel: _.toString(year),
        optionValue: year,
        displayLabel: {
          en: _.toString(year),
          es: _.toString(year),
        },
      }));
    };

    const ocaConfig = {
      ...ocaField,
      config: {
        ...ocaField.config,
        fieldName: ocaField.config.fieldName.slice(ocaField.config.fieldName.lastIndexOf('.') + 1),
      },
    };

    if (ocaField.config.yearsSettings) {
      ocaConfig.config.fieldOptions = getFieldOptionsByYearsSettings(ocaField.config.yearsSettings);
    }

    return ocaConfig;
  };

  const ocaTemplateFields = _(ocaTemplateJsonDefinition).flatMap('sections').flatMap('fields').value();
  const pcwFields = _(pcwJsonDefinition.sections).flatMap('blocks').flatMap('fields').value();
  const ocaTemplateFieldsHashMap = _.keyBy(ocaTemplateFields, 'config.fieldName');
  _.forEach(pcwFields, pcwField => {
    let keyToUse = pcwField.ambiguousKey || pcwField.key; // ambiguous key is in priority

    if (_.includes(keyToUse, 'primaryContact.')) {
      // hotfix - pcw adds primaryContact prefix for primaryContact and applicant fields
      keyToUse = keyToUse.slice(keyToUse.lastIndexOf('.') + 1);
    }

    const ocaField = ocaTemplateFieldsHashMap[keyToUse];
    if (!ocaField) {
      return;
    }
    pcwField.ocaConfig = getFieldOcaConfig(ocaField);
  });
};

const getItemDefaultConfig = itemType => {
  const getSectionDefaultConfig = () => {
    return {
      id: `section_${new Date().getTime()}_${_.random(1, 1000000)}`,
      config: {
        title: 'New Section',
        isCollapsed: false,
        rolesSettings: getItemDefaultRolesSettings(configurableItemsTypes.section),
      },
      blocks: [],
    };
  };

  const getBlockDefaultConfig = () => {
    return {
      id: `block_${new Date().getTime()}_${_.random(1, 1000000)}`,
      config: {
        title: 'New Block',
        rolesSettings: getItemDefaultRolesSettings(configurableItemsTypes.block),
      },
      fields: [],
    };
  };

  const getConfigByItemTypeHandlers = {
    [configurableItemsTypes.section]: getSectionDefaultConfig,
    [configurableItemsTypes.block]: getBlockDefaultConfig,
  };
  return getConfigByItemTypeHandlers[itemType] ? getConfigByItemTypeHandlers[itemType]() : null;
};

const getJSONDefinitionBasedOnOCATemplate = (ocaTemplate, portalConfigurationType) => {
  const isItemHiddenForConfigurationType = (item, isIndividualTemplate) => {
    if (item.sectionType === ocaSectionTypes.coApplicant) {
      return !isIndividualTemplate;
    }

    const bizOrIndividualRule = _.find(item.rules, { targetName: 'bizOrIndividual' });
    if (!bizOrIndividualRule) {
      return false;
    }
    return isIndividualTemplate
      ? bizOrIndividualRule.evalType === 'have_value' && _.includes(bizOrIndividualRule.evalValue, 'business')
      : bizOrIndividualRule.evalType === 'have_value' && _.includes(bizOrIndividualRule.evalValue, 'individual');
  };
  const getPCWNonOwnersSections = (ocaSections, isIndividualTemplate) => {
    const getSimpleBlock = ocaSectionFields => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: '',
        },
        fields: _.map(ocaSectionFields, ocaControl =>
          getFieldConfigBasedOnOCAControl(ocaControl, true, isIndividualTemplate)
        ),
      };
    };

    const getLegacyReferenceBlock = (ocaSectionFields, referenceType) => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: '',
        },
        type: blockTypes.reference,
        referenceType,
        fields: _.map(ocaSectionFields, ocaControl =>
          getFieldConfigBasedOnOCAControl(ocaControl, false, isIndividualTemplate)
        ),
      };
    };

    const getDynamicReferenceBlock = (ocaSection, ocaSectionFields) => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: '',
        },
        fields: _.map(ocaSectionFields, ocaControl =>
          getFieldConfigBasedOnOCAControl(ocaControl, false, isIndividualTemplate)
        ),
        type: blockTypes.dynamicReference,
        referenceType: ocaSection.referenceType,
        referenceIdx: ocaSection.referenceIdx,
      };
    };

    const getCoApplicantBlock = (ocaSection, ocaSectionFields) => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: '',
        },
        fields: _.map(ocaSectionFields, ocaControl =>
          getFieldConfigBasedOnOCAControl(ocaControl, false, isIndividualTemplate)
        ),
        type: blockTypes.coApplicant,
      };
    };

    const getEquipmentBlock = (ocaSection, ocaSectionFields) => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: '',
        },
        fields: _.map(ocaSectionFields, ocaControl =>
          getFieldConfigBasedOnOCAControl(ocaControl, false, isIndividualTemplate)
        ),
        type: blockTypes.equipment,
        equipmentType: ocaSection.equipmentType,
        equipmentIdx: ocaSection.equipmentIdx,
      };
    };

    const nonOwnersSections = _.reject(
      ocaSections,
      section => section.title.type === 'owners-array' || section.sectionType === ocaSectionTypes.marketingCommunication
    );
    return _(nonOwnersSections)
      .map(ocaSection => {
        const ocaSectionFields = _(ocaSection.fields)
          .reject(
            field =>
              isItemHiddenForConfigurationType(field, isIndividualTemplate) ||
              _.includes(fieldTypesToSkipForPCW, field.type)
          )
          .value();
        if (isItemHiddenForConfigurationType(ocaSection, isIndividualTemplate) || _.isEmpty(ocaSectionFields)) {
          return null;
        }
        const legacyReferenceField = _.find(
          ocaSectionFields,
          ocaField => referenceTypesByLegacyFieldsKey[ocaField.config.fieldName]
        );
        const isLegacyReferenceBlock = !!legacyReferenceField;
        const isDynamicReferenceBlock = ocaSection.sectionType === ocaSectionTypes.dynamicReference;
        const isCoApplicantBlock = ocaSection.sectionType === ocaSectionTypes.coApplicant;
        const isEquipmentBlock = ocaSection.sectionType === ocaSectionTypes.equipment;
        let block;
        switch (true) {
          case isLegacyReferenceBlock:
            block = getLegacyReferenceBlock(
              ocaSectionFields,
              referenceTypesByLegacyFieldsKey[legacyReferenceField.config.fieldName]
            );
            break;
          case isDynamicReferenceBlock:
            block = getDynamicReferenceBlock(ocaSection, ocaSectionFields);
            break;
          case isCoApplicantBlock:
            block = getCoApplicantBlock(ocaSection, ocaSectionFields);
            break;
          case isEquipmentBlock:
            block = getEquipmentBlock(ocaSection, ocaSectionFields);
            break;
          default:
            block = getSimpleBlock(ocaSectionFields);
        }
        const defaultSectionConfig = getItemDefaultConfig(configurableItemsTypes.section);
        return {
          ...defaultSectionConfig,
          config: {
            ...defaultSectionConfig.config,
            title: _.get(ocaSection, 'title.config.displayLabel.en'),
          },
          blocks: [block],
        };
      })
      .compact()
      .value();
  };
  const getPCWOwnersSection = (ocaSections, isIndividualTemplate) => {
    const getOwnerBlock = ocaControls => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      const fields = _(ocaControls)
        .map(ocaControl => getFieldConfigBasedOnOCAControl(ocaControl, false, isIndividualTemplate))
        .sortBy(field => (_.includes(['signatureDate', 'personalGuaranteeSignatureDate'], field.key) ? 1 : 0)) // move signatureDate to the last position
        .value();
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: isIndividualTemplate ? 'Applicant {#}' : 'Owner {#}',
        },
        type: blockTypes.owner,
        fields,
      };
    };
    if (isIndividualTemplate) {
      return []; // TODO: remove other logic related to isIndividualTemplate if current approach will work
    }
    const ownersSections = _.filter(ocaSections, section => section.title.type === 'owners-array');
    const ownersSectionFields = _(ownersSections)
      .flatMap('fields')
      .reject(f => _.includes(fieldTypesToSkipForPCW, f.type))
      .uniqBy('config.fieldName')
      .value();
    if (_.isEmpty(ownersSectionFields)) {
      return null;
    }
    const defaultSectionConfig = getItemDefaultConfig(configurableItemsTypes.section);
    return {
      ...defaultSectionConfig,
      config: {
        ...defaultSectionConfig.config,
        title: isIndividualTemplate ? '' : 'Ownership Information',
      },
      blocks: [getOwnerBlock(ownersSectionFields)],
    };
  };
  const getDisclosuresSection = () => {
    const disclosuresBlocksSettings = [
      {
        title: 'Authorization Language',
        fieldsSettings: [{ key: 'disclosure.authorizationLanguage' }],
      },
      {
        title: 'Terms And Conditions Language',
        fieldsSettings: [{ key: 'disclosure.termsAndConditionsLanguage' }],
      },
    ];
    const defaultSectionConfig = getItemDefaultConfig(configurableItemsTypes.section);
    const disclosuresBlocks = _.map(disclosuresBlocksSettings, blockSetting => {
      const defaultBlockConfig = getItemDefaultConfig(configurableItemsTypes.block);
      return {
        ...defaultBlockConfig,
        config: {
          ...defaultBlockConfig.config,
          title: blockSetting.title,
        },
        fields: _.map(blockSetting.fieldsSettings, fieldSettings => {
          return {
            key: fieldSettings.key,
            config: {
              title: '',
              rolesSettings: getItemDefaultRolesSettings(configurableItemsTypes.field),
            },
          };
        }),
      };
    });
    return {
      ...defaultSectionConfig,
      config: {
        ...defaultSectionConfig.config,
        title: 'Disclosures',
        isCollapsed: true,
      },
      type: sectionTypes.disclosures,
      blocks: disclosuresBlocks,
    };
  };
  const isSectionToReject = section => {
    const sectionTypesToReject = [ocaSectionTypes.dynamicSignatures];
    return (
      _.includes(sectionTypesToReject, section.sectionType) || _.get(section, 'title.type') === 'signatures-simple'
    );
  };

  const isIndividualTemplate = portalConfigurationType === portalConfigurationTypes.CADMIndividual;
  const jsonDefinition = getCreditAppModalDefaultJSONDefinition();
  const ocaSections = _(ocaTemplate.jsonDefinition)
    .reject(page => isItemHiddenForConfigurationType(page, isIndividualTemplate))
    .flatMap('sections')
    .reject(isSectionToReject)
    .value();

  const pcwNonOwnersSections = getPCWNonOwnersSections(ocaSections, isIndividualTemplate);
  const pcwOwnersSection = getPCWOwnersSection(ocaSections, isIndividualTemplate);
  const disclosuresSection = getDisclosuresSection();
  jsonDefinition.sections = _(pcwNonOwnersSections)
    .concat(pcwOwnersSection)
    .concat([disclosuresSection])
    .compact()
    .value();
  return jsonDefinition;
};

const getFieldConfigBasedOnOCAControl = (ocaControl, isSimpleBlock, isIndividualTemplate) => {
  const getHtmlOutputFieldConfig = ocaControl => {
    return {
      key: _.get(ocaControl, 'config.fieldName', null),
      selectedApiId: _.get(ocaControl, 'config.selectedApiId'),
      customMarkup: _.get(ocaControl, 'config.customMarkup'),
      config: {
        title: _.get(ocaControl, 'config.displayLabel.en', null),
        rolesSettings: getItemDefaultRolesSettings(configurableItemsTypes.field),
      },
    };
  };

  const getDefaultFieldConfig = (ocaControl, isIndividualTemplate) => {
    /**
     * We have some ambigious mappings. For example, the data entered in homeAddress actually falls into address field.
     * In this method we replace the keys for such fields in order to show entered data.
     */
    const getOCAControlWithUpdatedFieldName = (ocaControl, isIndividualTemplate) => {
      let fieldName = _.get(ocaControl, 'config.fieldName', null);
      if (isSimpleBlock) {
        if (isIndividualTemplate && _.includes(pocFields, fieldName)) {
          fieldName = 'primaryContact.' + fieldName;
        }
        if (
          !isIndividualTemplate &&
          _.includes(_.difference(pocFields, fieldsToNotPopulateFromPocForBusinessApp), fieldName)
        ) {
          fieldName = 'primaryContact.' + fieldName;
        }
      }
      const keysToReplace = _.keys(ambiguousFieldsToReplaceMap);
      const isAmbigiousMapping = _.includes(keysToReplace, fieldName);
      const fieldNameToSet = isAmbigiousMapping ? ambiguousFieldsToReplaceMap[fieldName] : fieldName;
      return {
        ...ocaControl,
        config: {
          ...ocaControl.config,
          fieldName: fieldNameToSet,
          ambigiousFieldName: isAmbigiousMapping ? fieldName : null,
        },
      };
    };

    const setFieldDefaultRolesSettings = field => {
      if (_.includes(fieldsForCreditMgrsOnly, field.ambiguousKey) || _.includes(fieldsForCreditMgrsOnly, field.key)) {
        _.set(field, ['config', 'rolesSettings', rolesSettingsKeys.masked], nonCreditMgrsRoles);
      }
      if (_.includes(hiddenFields, field.key)) {
        _.set(field, ['config', 'rolesSettings', rolesSettingsKeys.hidden], _.values(vendorContactRole));
      }
      if (_.includes(hiddenFieldsOnPDF, field.key)) {
        _.set(field, ['config', 'rolesSettings', rolesSettingsKeys.hiddenOnPDF], _.values(vendorContactRole));
      }
      _.forEach(readOnlyFieldsByRoles, (readOnlyRoles, readOnlyFieldKey) => {
        if (readOnlyFieldKey === field.key) {
          _.set(field, ['config', 'rolesSettings', rolesSettingsKeys.readOnly], readOnlyRoles);
        }
      });
    };

    ocaControl = getOCAControlWithUpdatedFieldName(ocaControl, isIndividualTemplate);
    const field = {
      key: _.get(ocaControl, 'config.fieldName', null),
      ambiguousKey: _.get(ocaControl, 'config.ambigiousFieldName', null),
      config: {
        title: _.get(ocaControl, 'config.displayLabel.en', null),
        rolesSettings: getItemDefaultRolesSettings(configurableItemsTypes.field),
      },
    };
    setFieldDefaultRolesSettings(field);
    return field;
  };

  const getConfigsByOcaControlType = {
    'html-output-field': getHtmlOutputFieldConfig,
  };
  return getConfigsByOcaControlType[ocaControl.type]
    ? getConfigsByOcaControlType[ocaControl.type](ocaControl)
    : getDefaultFieldConfig(ocaControl, isIndividualTemplate);
};

const isItemHasRestrictionsByRoles = itemConfig => {
  return _(itemConfig.rolesSettings).some(_.some);
};

const getConfigurableItemWarnMessage = (item, itemType) => {
  const { config } = item;
  const hasSomeRestrictionsByUserRoles = isItemHasRestrictionsByRoles(config);
  return hasSomeRestrictionsByUserRoles ? `This ${itemType} has some restrictions by user roles` : null;
};

const isItemHasRestrictionForRole = (item, roleSettingKey, role) => {
  const rolesRestriction = _.get(item, ['config', 'rolesSettings', roleSettingKey]);
  return _.includes(rolesRestriction, role);
};

const getOwnerTheSameAsPOC = (pocEmail, owners) => {
  return pocEmail && _.find(owners, o => _.toLower(o.email) === _.toLower(pocEmail));
};

const getCreditAppHTMLForPDF = (
  sectionsHTML,
  creditAppModalData,
  vendorProfile,
  account,
  userProfile,
  isIndividualCreditApp
) => {
  const getPotentialSignors = () => {
    if (isIndividualCreditApp) {
      return creditAppModalData.owners;
    }

    // hotfix: needs to be reworked
    if (_.isEmpty(creditAppModalData.primaryContact)) {
      return creditAppModalData.owners;
    }

    return !creditAppModalData.primaryContact.pcOnly // poc is the same as owner
      ? creditAppModalData.owners
      : [creditAppModalData.primaryContact, ...creditAppModalData.owners];
  };

  const getAuthorizationAndAcceptanceLanguage = () => {
    const dynamicDisclosure = creditAppModalData.disclosure;
    const dynamicDisclosureAuthorizationLanguage = _.get(dynamicDisclosure, 'authorizationLanguage', null);
    const vpAuthorizationLanguage = _.get(vendorProfile, 'dcrDisclosureLanguage', null);
    return (
      dynamicDisclosureAuthorizationLanguage ||
      vpAuthorizationLanguage ||
      `
              Applicant has provided the following authorization: By signing above and submitting this Application, each owner, officer or guarantor listed above agree, individually and
              as the duly authorized representative of the business-applicant (i) that all information provided in this Application is true, correct and complete, (ii) that the property leased
              or any credit extended by Trnsact, Inc ("Trnsact") will be used solely for business and commercial purposes. The business-applicant and each owner, officer
              or guarantor listed above further authorize Trnsact or its successors, assigns or one or more alternate third-party funding providers to investigate its organizational and personal
              credit histories, including, but not limited to, obtaining a credit report from a credit reporting agency, from time to time and for any lawful purposes.
              `
    );
  };

  const getTermsAndConditionsLanguage = () => {
    const dynamicDisclosure = creditAppModalData.disclosure;
    const dynamicDisclosureTermsAndConditionsLanguage = _.get(dynamicDisclosure, 'termsAndConditionsLanguage', null);
    const vpTermsAndConditionsLanguage = _.get(vendorProfile, 'dcrDisclosureTerms', null);
    return dynamicDisclosureTermsAndConditionsLanguage || vpTermsAndConditionsLanguage;
    /*     return (
      dynamicDisclosureTermsAndConditionsLanguage ||
      (vendorProfile.dcrDisclosureUseDefaultTerms ? null : vpTermsAndConditionsLanguage)
    ); */
  };

  const getAuthorizationSignaturesSection = () => {
    const getSignorBlock = (signor, idx) => {
      const commercialLabelsBySignorIndex = {
        // todo: improve
        //"poc": "Primary Contact",
        0: 'First Signor',
        1: 'Second Signor',
        2: 'Third Signor',
        3: 'Fourth Signor',
        4: 'Fifth Signor',
        5: 'Sixth Signor',
      };
      const individualLabelsBySignorIndex = {
        0: 'First Applicant',
        1: 'Second Applicant',
        2: 'Third Applicant',
        3: 'Fourth Applicant',
        4: 'Fifth Applicant',
        5: 'Sixth Applicant',
      };
      const labelsToUse = isIndividualCreditApp ? individualLabelsBySignorIndex : commercialLabelsBySignorIndex;
      const signature = _.get(signor, 'signature') || '';
      const title = _.get(signor, 'title') || '';
      const ipAddress = _.get(signor, 'ipAddress') || '';
      const signatureDate = getDateTimeDisplayValue(_.get(signor, 'signatureDate')) || '';
      return `
      <div class="signatureRow">
        <div class="applicant">
          <div class="signatureBlock">
            <div class="authorizationLabel">${labelsToUse[_.toString(idx)]}:</div>
            <div class="authorizationData signature">${signature}</div>
          </div>
          <div class="titleBlock">
            ${
              !isIndividualCreditApp
                ? `<div class="authorizationLabel">Title:</div>
            <div class="authorizationData">${title}</div>`
                : ``
            }
          </div>
          <div class="dateBlock">
            <div class="authorizationLabel">Date:</div>
            <div class="authorizationData"> ${signatureDate}</div>
          </div>
         
        </div>
        <div class="ipAddress">
          <div class="authorizationLabel">${labelsToUse[_.toString(idx)]} IP Address:</div>
          <div class="authorizationData"> ${ipAddress}</div>
        </div>
      </div>
        
      `;
    };

    const potentialSignors = getPotentialSignors();
    return `
      <div class="signaturesSection">
        ${_(potentialSignors)
          .filter(signor => signor.signature)
          .uniqBy(
            signor =>
              [
                _.trim(_.toLower(signor.email)),
                _.trim(_.toLower(signor.firstName)),
                _.trim(_.toLower(signor.lastName)),
              ].join() // to reject additional rows when poc = some of the owner (checking by uniq email, firstName and lastName)
          )
          .map(getSignorBlock)
          .join(' ')}
      </div>
    `;
  };

  const getPersonalGuaranteeSignaturesSection = () => {
    const getSignorBlock = signor => {
      const personalGuaranteeSignature = _.get(signor, 'personalGuaranteeSignature') || '';
      const ipAddress = _.get(signor, 'ipAddress') || '';
      const personalGuaranteeSignatureDate =
        getDateTimeDisplayValue(_.get(signor, 'personalGuaranteeSignatureDate')) || '';
      if (!personalGuaranteeSignature) {
        return '';
      }
      return `
        <div class="signatureBlock ">
          <div class="applicant">
            <div class="signatureBlock">
              <div class="authorizationLabel">Personal Guarantee: </div>
              <div class="authorizationData signature">${personalGuaranteeSignature}</div>
            </div>
            <div class="dateBlock">
              <div class="authorizationLabel">Date:</div>
              <div class="authorizationData"> ${personalGuaranteeSignatureDate}</div>
            </div>
            <div class="dateBlock">
              <div class="authorizationLabel">IP:</div>
              <div class="authorizationData"> ${ipAddress}</div>
            </div>
          </div>
        </div>
      `;
    };

    const potentialSignors = getPotentialSignors();
    return `
      <div class="signaturesSection personalGuaranteeSignatures">
        ${_(potentialSignors)
          .filter(signor => signor.personalGuaranteeSignature)
          .uniqBy(
            signor =>
              [
                _.trim(_.toLower(signor.email)),
                _.trim(_.toLower(signor.firstName)),
                _.trim(_.toLower(signor.lastName)),
              ].join() // to reject additional rows when poc = some of the owner (checking by uniq email, firstName and lastName)
          )
          .map(getSignorBlock)
          .join(' ')}
      </div>
    `;
  };

  const getPersonalGuaranteeAgreementHTML = () => {
    const potentialSignors = getPotentialSignors();
    const hasPersonalGuaranteeSignature = _.some(potentialSignors, 'personalGuaranteeSignature');
    if (!hasPersonalGuaranteeSignature || !vendorProfile.ocaPersonalGuaranteeAgreementDisclosure) {
      return '';
    }
    return `
      <div class="sectionWrapper personalGuaranteeAgreementDisclosureSection">
        <div class="sectionTitle"></div>
        <div class="blockWrapper">
          <h3 class="blockTitle">Personal Guarantee Agreement</h3>
          <div class="agreement-text">
            ${vendorProfile.ocaPersonalGuaranteeAgreementDisclosure}
          </div>
        </div>
      </div>
    `;
  };

  const headHTML = `
    <head>
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap" rel="stylesheet">
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=La+Belle+Aurore">
    </head>
  `;
  const stylesHTML = `
    <style>
      
      .header {
        display: flex;
        margin-bottom: 16px;
      }
      .header img {
        height: 100px;
        width: 200px;
        object-fit: contain;
      }
      .headerInfoWrapper {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: auto;
        margin-left: 32px;
      }
      .headerInfoWrapper h1 {
        font-size: 40px;
        font-optical-sizing: auto;
        font-family: "Archivo", sans-serif;
        margin: 0;
        font-variant: small-caps;
    }
      .creditApplicationInfo {
        display: flex;
        justify-content: space-between;
        font-size: 14px;
      }
      .sectionWrapper {
        font-family: "Roboto", sans-serif;
        font-style: normal;
        font-size:12px;
        margin-bottom: 10px;
        border: 1px solid #83879b;
        border-radius: 8px;
        background-color: #f2f2f2;
        padding: 8px;
      }
      .sectionWrapper .sectionTitle {
        border-radius: 8px;
        break-after: avoid;
      }
      .sectionWrapper .sectionTitle h2 {
          margin: 0;
          font-family: "Archivo", sans-serif;
          font-size: 18px;
          color: #0e0d0d;
          font-variant: small-caps;
          text-decoration:underline;
      }
      .collapseButton {
        display: none;
      }
      .blockWrapper {
        margin-top: 8px;
        border: 1px solid #d5d5d5;
        border-radius: 8px;
        background-color: #d2d8d9;
        break-inside: avoid;
      }
      .blockTitle {
        margin: 0;
        padding: 8px;
        font-size: 12px;
      }
      .fieldRow {
        padding: 4px 6px;
        display: flex;
        align-items: center;
        flex: 1 0 25%;
        //white-space: nowrap;
        font-size: 12px;
        box-shadow: 1px 0 0 0 #d5d5d5, 0 1px 0 0 #d5d5d5, 1px 1px 0 0 #d5d5d5, 1px 0 0 0 #d5d5d5 inset, 0 1px 0 0 #d5d5d5 inset;
      }
      .fieldTitleWrapper {
        margin-right: 6px;
        font-weight: bold;
      }
      .fieldTitleWrapper:after {
        content: ":"
      }
      .fieldsWrapper {
        display: flex;
        flex-wrap: wrap;
        background-color: white;
        padding:10px;
      }
      .fieldValueWrapper {
        font-family: Open Sans, Arial, sans-serif;
      }
      .disclosures-section {
        display: none;  /* We generate disclosures sections manually for PDF*/
      }
      .termsAndConditionsSection, .personalGuaranteeAgreementDisclosureSection {
        break-before: page;
      }
      .agreement-text {
        padding: 10px;
        background: white;
        margin: 0 10px 10px 10px;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        font-size: 12px;
      }
      footer {
        display: flex;
        justify-content: space-between;
        margin-top: 50px;
        page-break-inside: avoid;
      }
      .trnsactLogo {
        display: flex;
        align-items: center;
      }
      .accessedOnWrapper {
        display: flex;
        flex-direction: column;
      }
      .accessedOnWrapper span {
        margin-bottom: 12px;
        padding-bottom: 2px;
        border-bottom: 1px solid black;
        font-size: 12px;
      }
      .declarationAndSignatures {
        break-inside: avoid;
      }
      .applicationDate .date, .applicationType .type {
        text-decoration: underline;
      }
      .hiddenOnPdf {
        display: none;
      }
      @page {
        margin: 24px 0 32px;
      }


      html {
        font-family: arial;
        margin-left: 15px;
        margin-right: 15px;
      }
      .authorizationLabel {
        font-family:"Roboto", sans-serif;
        font-weight: 800;
        font-style: normal;
        height: 20px;
      }
      .authorizationData {
        border-bottom: 1px solid black;
        text-align: center;
        margin-left: 5px;
        margin-right: 8px;
        height: 20px;
        flex: 1 0 auto;
      }
      .signature {
        font-family: 'La Belle Aurore', cursive;
        font-size: 20px;
        padding-bottom: 5px;
      }
     
      .signatureRow {
        margin: 5px 0;
        border-radius: 5px;
        padding: 5px;
        box-shadow: 3px 5px 10px 1px #6f8185, 0 1px 0 0 #6f8185, 1px 1px 0 0 #6f8185, 1px 0 0 0 #6f8185 inset, 0 1px 0 0 #6f8185 inset;
    }
      .signaturesSection {
        display:flex;
        flex-direction: column;
        font-size: 12px;
        break-inside: avoid;
        margin-top: 20px;
      }
      .signaturesSection .applicant {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr;
        margin-bottom: 20px;
      }
      .signatureBlock, .titleBlock, .dateBlock {
        display: flex;
        align-items: center;
      }
      .signaturesSection.personalGuaranteeSignatures .applicant {
        grid-template-columns: 2fr 1fr 0.8fr;
        width: 100%
      }
      .dateBlock {
        justify-content: flex-end;
      }
      .ipAddress {
        width: 300px;
        margin: 0 0 10px 0;
        display: flex;
    }
     
        
      
    </style>
  `;
  const headerHTML = `
    <div class="header">
      <div class="headerImageWrapper">
        <img src="${`https://${config.S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${vendorProfile.logo}`}" />
      </div>
      <div class="headerInfoWrapper ">
        <h1>Credit Application</h1>
        <div class="creditApplicationInfo">
          <div class="applicationType">
            <strong>Application Type:</strong>
            <span class="type">
              ${_.startCase(_.lowerCase(creditAppModalData.applicationType))}
            </span>
          </div>
          <div class="applicationDate">
            <strong>Application Date:</strong>
            <span class="date">
              ${
                creditAppModalData.recertificationDate
                  ? getDateTimeDisplayValue(creditAppModalData.recertificationDate, 'MM-dd-yyyy')
                  : creditAppModalData.createdDateTime
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  `;
  const authorizationAndAcceptanceLanguage = getAuthorizationAndAcceptanceLanguage();
  const authorizationLanguageHTML = `
    <div class="sectionWrapper authorizationLanguageSection ">
      <div class="sectionTitle"></div>
      <div class="blockWrapper">
        <h3 class="blockTitle">Authorization And Acceptance</h3>
        <div class="agreement-text">
          ${authorizationAndAcceptanceLanguage}
        </div>
      </div>
    </div>
  `;

  const personalGuaranteeAgreementHTML = getPersonalGuaranteeAgreementHTML();
  const authorizationSignaturesSection = getAuthorizationSignaturesSection();
  const personalGuaranteeSignaturesSection = getPersonalGuaranteeSignaturesSection();
  const dynamicDisclosureTermsAndConditionsLanguage = getTermsAndConditionsLanguage();
  const termsAndConditionsLanguageHTML = dynamicDisclosureTermsAndConditionsLanguage
    ? `
      <div class="sectionWrapper termsAndConditionsSection ">
        <div class="sectionTitle"></div>
        <div class="blockWrapper">
          <h3 class="blockTitle">Additional Terms And Conditions</h3>
          <div class="agreement-text">
            ${dynamicDisclosureTermsAndConditionsLanguage}
          </div>
        </div>
      </div>
    `
    : ``;
  const declarationAndSignatures = `
      <div class="declarationAndSignatures  ">
        ${
          authorizationLanguageHTML +
          authorizationSignaturesSection +
          personalGuaranteeAgreementHTML +
          personalGuaranteeSignaturesSection +
          termsAndConditionsLanguageHTML
        }
      </div>
  `;
  const footerHTML = `
    <footer>  
      <div class="trnsactLogo">
        <img width="145" height="50" src="data:image/png;base64,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" />
      </div>
      <div class="accessedOnWrapper">
        <span>Accessed On: ${format(new Date(), 'MM-dd-yyyy')}</span>
        ${!_.isEmpty(userProfile) ? `<span>Accessed By: ${userProfile.fullName}</span>` : ``}
        <span>Organization: ${account.name}</span>
      </div>
    </footer>
  `;

  const html = _.join([headHTML, stylesHTML, headerHTML, sectionsHTML, declarationAndSignatures, footerHTML], '');
  return html.replace(/(\r\n|\n|\r)/gm, '');
};

const getDateTimeDisplayValue = (timeStamp, formatString = 'MM-dd-yyyy h:mm a') => {
  if (!timeStamp) {
    return '';
  }
  return format(_.parseInt(timeStamp), formatString);
};

const getSignatureTaskForContact = (
  owner,
  vendorOpportunityId,
  dynamicsCreditApplicationId,
  account,
  currentUserProfile
) => {
  return {
    ownerPgId: owner.ownerPgId,
    vendorOpportunityId,
    dynamicsCreditApplicationId,
    regarding: taskRegardingValues.signatures,
    assignedToUser: owner.firstName + ' ' + owner.lastName,
    assignedToUserProfileId: null,
    assignedToAccountId: _.get(account, 'id'),
    assignedToAccount: _.get(account, 'name'),
    assignedByUserProfileId: currentUserProfile.id,
    assignedByAccountId: _.get(account, 'id'),
    assignedByAccount: _.get(account, 'name'),
    assignedByUser: currentUserProfile.fullName,
    createdBy: currentUserProfile.fullName,
    status: 'open',
    priority: 'high',
    notifyByEmail: true,
    notifyAssignorOnReminder: true,
    emailTemplateId: null,
    assignedToApplicant: true,
    assignedBySystem: false,
    dueDateTime: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString(),
    taskItems: [{ type: 'info', taskAssignorNotes: 'Please review credit application and provide signature.' }],
  };
};

const getNewSignorAndGuarantorSignatureTaskForContact = (
  owner,
  vendorOpportunityId,
  dynamicsCreditApplicationId,
  account,
  currentUserProfile
) => {
  const signatureTask = getSignatureTaskForContact(
    owner,
    vendorOpportunityId,
    dynamicsCreditApplicationId,
    account,
    currentUserProfile
  );
  return {
    ...signatureTask,
    regarding: taskRegardingValues.newSignorAndGuarantorSignature,
  };
};

const getRecertificationTaskForContact = (
  owner,
  vendorOpportunityId,
  dynamicsCreditApplicationId,
  account,
  currentUserProfile
) => {
  return {
    ownerPgId: owner.ownerPgId,
    vendorOpportunityId,
    dynamicsCreditApplicationId,
    regarding: taskRegardingValues.recertificationSignatures,
    assignedToUser: owner.firstName + ' ' + owner.lastName,
    assignedToUserProfileId: null,
    assignedToAccountId: _.get(account, 'id'),
    assignedToAccount: _.get(account, 'name'),
    assignedByUserProfileId: currentUserProfile.id,
    assignedByAccountId: _.get(account, 'id'),
    assignedByAccount: _.get(account, 'name'),
    assignedByUser: currentUserProfile.fullName,
    createdBy: currentUserProfile.fullName,
    status: 'open',
    priority: 'high',
    notifyByEmail: true,
    notifyAssignorOnReminder: true,
    emailTemplateId: 'd-9c36e246ab6e4940907037a16f8667e6',
    assignedToApplicant: true,
    assignedBySystem: false,
    dueDateTime: new Date(new Date().setDate(new Date().getDate() + 7)).toISOString(),
    taskItems: [{ type: 'info', taskAssignorNotes: 'Please review credit application for recertification.' }],
  };
};

export {
  // methods
  getCreditAppModalDefaultPortalConfiguration,
  getItemDefaultConfig,
  assignFieldsOCAConfig,
  assignFieldsOCAConfigFromActiveTemplate,
  getJSONDefinitionBasedOnOCATemplate,
  getFieldConfigBasedOnOCAControl,
  getCreditAppHTMLForPDF,
  getConfigurableItemWarnMessage,
  isItemHasRestrictionForRole,
  getDateTimeDisplayValue,
  getOwnerTheSameAsPOC,
  getSignatureTaskForContact,
  getNewSignorAndGuarantorSignatureTaskForContact,
  getRecertificationTaskForContact,
  // constants
  defaultMovingElementSettings,
  refSettingsByRefType,
  blockTypes,
  sectionTypes,
  uneditableFieldTypes,
  uneditableFieldKeys,
};
